import { Form, Card, Alert, Button, Spinner, Badge } from 'react-bootstrap'
import { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import {FormattedMessage, useIntl} from 'react-intl'
import {ONE_USER_MESSAGES as M} from '../oneUserMessages'

// Emoji y simbolos sueltos: el rule los marca dentro del JSX.
const EMOJI_ROBOT = '🤖'
const EMOJI_EYE = '👁️‍🗨️'
const EMOJI_LOCK = '🔒'
const EMOJI_DISK = '💾'
const EMOJI_WARNING = '⚠️'
const EMOJI_BULB = '💡'
const ASTERISK = '*'
const DIFY_DASHBOARD_URL = 'https://cloud.dify.ai'
const DIFY_DEFAULT_API_URL = 'https://api.dify.ai/v1'

interface UserDifyConfigTabProps {
  userId: number
  orgDifyEnabled: boolean
}

export const UserDifyConfigTab = ({ userId, orgDifyEnabled }: UserDifyConfigTabProps) => {
  const intl = useIntl()
  const [usePersonal, setUsePersonal] = useState(false)
  const [difyApiKey, setDifyApiKey] = useState('')
  const [difyApiUrl, setDifyApiUrl] = useState('https://api.dify.ai/v1')
  const [difyAppId, setDifyAppId] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showApiKey, setShowApiKey] = useState(false)

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`/api/users/${userId}/dify-config/`)
        setUsePersonal(response.data.use_personal || false)
        setDifyApiKey(response.data.dify_api_key || '')
        setDifyApiUrl(response.data.dify_api_url || 'https://api.dify.ai/v1')
        setDifyAppId(response.data.dify_app_id || '')
      } catch (error) {
        console.warn('No Dify config found for user, using defaults')
      } finally {
        setLoading(false)
      }
    }

    loadConfig()
  }, [userId])

  const handleSave = async () => {
    if (usePersonal && !difyApiKey) {
      toast.warning(intl.formatMessage(M.difyToastEnterApiKey))
      return
    }

    setSaving(true)
    try {
      await axios.post(`/api/users/${userId}/dify-config/`, {
        use_personal: usePersonal,
        dify_api_key: difyApiKey,
        dify_api_url: difyApiUrl,
        dify_app_id: difyAppId,
      })
      toast.success(intl.formatMessage(M.difyToastSaved))
    } catch (error: any) {
      toast.error(
        intl.formatMessage(M.difyToastSaveError, {
          message: error.response?.data?.error || error.message,
        })
      )
    } finally {
      setSaving(false)
    }
  }

  const handleTestConnection = async () => {
    if (!difyApiKey || !difyApiUrl) {
      toast.warning(intl.formatMessage(M.difyToastEnterKeyAndUrl))
      return
    }

    try {
      await axios.get(`${difyApiUrl}/parameters`, {
        headers: { Authorization: `Bearer ${difyApiKey}` },
        timeout: 5000,
      })
      toast.success(intl.formatMessage(M.difyToastVerified))
    } catch (error: any) {
      const errorMsg =
        error.response?.status === 401
          ? intl.formatMessage(M.difyInvalidApiKey)
          : error.message || intl.formatMessage(M.difyConnectionError)
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: errorMsg}))
    }
  }

  if (loading) {
    return (
      <div className="pt-4">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div className="pt-4">
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">
            <span className="me-2">{EMOJI_ROBOT}</span>
            <FormattedMessage {...M.difyTitle} />
          </h5>
        </Card.Header>
        <Card.Body>
          {orgDifyEnabled ? (
            <>
              <Alert variant="info" className="mb-4">
                <small>
                  <FormattedMessage {...M.difyOrgEnabled} />
                </small>
              </Alert>

              <Form.Group className="mb-4">
                <Form.Check
                  type="switch"
                  label={intl.formatMessage(M.difyUseOwn)}
                  checked={usePersonal}
                  onChange={(e) => setUsePersonal(e.target.checked)}
                  id="dify-personal-switch"
                />
                <Form.Text className="text-muted d-block mt-2">
                  <FormattedMessage {...M.difyUseOwnHint} />
                </Form.Text>
              </Form.Group>

              {usePersonal && (
                <>
                  {/* API Key */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FormattedMessage {...M.difyApiKey} />
                      <span className="text-danger ms-1">{ASTERISK}</span>
                    </Form.Label>
                    <div className="input-group">
                      <Form.Control
                        type={showApiKey ? 'text' : 'password'}
                        value={difyApiKey}
                        onChange={(e) => setDifyApiKey(e.target.value)}
                        placeholder="app-xxxxx"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowApiKey(!showApiKey)}
                        title={intl.formatMessage(showApiKey ? M.difyHide : M.difyShow)}
                      >
                        {showApiKey ? EMOJI_EYE : EMOJI_LOCK}
                      </Button>
                    </div>
                    <Form.Text className="text-muted d-block mt-2">
                      <FormattedMessage
                        {...M.difyApiKeyHint}
                        values={{
                          a: (chunks: React.ReactNode) => (
                            <a href={DIFY_DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
                              {chunks}
                            </a>
                          ),
                        }}
                      />
                    </Form.Text>
                  </Form.Group>

                  {/* API URL */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FormattedMessage {...M.difyApiUrl} />
                    </Form.Label>
                    <Form.Control
                      type="url"
                      value={difyApiUrl}
                      onChange={(e) => setDifyApiUrl(e.target.value)}
                      placeholder="https://api.dify.ai/v1"
                    />
                    <Form.Text className="text-muted d-block mt-2">
                      <FormattedMessage {...M.difyApiUrlHint} values={{url: DIFY_DEFAULT_API_URL}} />
                    </Form.Text>
                  </Form.Group>

                  {/* App ID */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FormattedMessage {...M.difyAppId} />
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={difyAppId}
                      onChange={(e) => setDifyAppId(e.target.value)}
                      placeholder={intl.formatMessage(M.difyAppIdPlaceholder)}
                    />
                    <Form.Text className="text-muted d-block mt-2">
                      <FormattedMessage {...M.difyAppIdHint} />
                    </Form.Text>
                  </Form.Group>

                  {/* Test Connection Button */}
                  <div className="mb-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleTestConnection}
                      disabled={!difyApiKey}
                      className="me-2"
                    >
                      {EMOJI_DISK} <FormattedMessage {...M.difyTestConnection} />
                    </Button>
                  </div>

                  {/* Warning Box */}
                  <div className="bg-warning bg-opacity-10 p-3 rounded border-start border-warning border-4 mb-3">
                    <small className="text-dark d-block">
                      <strong>
                        {EMOJI_WARNING} <FormattedMessage {...M.difyImportant} />
                      </strong> <FormattedMessage {...M.difyStoredSecurely} />
                    </small>
                  </div>
                </>
              )}

              {/* Save Button */}
              {usePersonal && (
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        <FormattedMessage {...M.difySaving} />
                      </>
                    ) : (
                      <>
                        {EMOJI_DISK} <FormattedMessage {...M.difySaveChanges} />
                      </>
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setUsePersonal(false)}
                  >
                    <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                  </Button>
                </div>
              )}

              {!usePersonal && (
                <div className="bg-light p-3 rounded border-start border-info border-4">
                  <small className="text-muted d-block">
                    <strong>
                      {EMOJI_BULB} <FormattedMessage {...M.difyNote} />
                    </strong> <FormattedMessage {...M.difyUsingOrg} />
                  </small>
                </div>
              )}
            </>
          ) : (
            <Alert variant="warning">
              <small>
                {EMOJI_WARNING} <FormattedMessage {...M.difyNotEnabled} />
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
